import { useEffect } from 'react'
import { dia } from '@joint/core'

interface UseElementHighlightingProps {
  graph: dia.Graph | null
  paper: dia.Paper | null
  selectedElementIds: string[]
  hoveredElementId: string | null
}

/**
 * Custom hook that manages element highlighting effects
 * - Multi-select highlighting with CSS classes
 * - Hover highlighting with dynamic outline-offset
 */
export const useElementHighlighting = ({
  graph,
  paper,
  selectedElementIds,
  hoveredElementId,
}: UseElementHighlightingProps): void => {
  // Multi-select highlighting effect
  useEffect(() => {
    if (!graph || !paper || selectedElementIds.length === 0) return

    // Add highlight class to all selected elements
    selectedElementIds.forEach((elementId) => {
      const element = graph.getCell(elementId)
      if (!element || !element.isElement()) return

      const view = paper.findViewByModel(element)
      if (!view || !view.el) return

      view.el.classList.add('multi-select-highlight')
    })

    // Cleanup: remove highlight when selection changes
    return () => {
      selectedElementIds.forEach((elementId) => {
        const element = graph.getCell(elementId)
        if (!element || !element.isElement()) return

        const view = paper.findViewByModel(element)
        if (view && view.el) {
          view.el.classList.remove('multi-select-highlight')
        }
      })
    }
  }, [selectedElementIds, graph, paper])

  // Hover highlighting effect
  useEffect(() => {
    if (!graph || !paper || !hoveredElementId) return

    // Find the element in graph
    const element = graph.getCell(hoveredElementId)
    if (!element || !element.isElement()) return

    // Get the element view from paper
    const view = paper.findViewByModel(element)
    if (!view || !view.el) return

    // Add CSS class for hover highlight
    view.el.classList.add('object-hover-highlight')

    // Calculate dynamic outline-offset based on element size
    const size = element.size()
    const minDimension = Math.min(size.width, size.height)
    const offset = Math.max(0.5, Math.min(4, minDimension * 0.015))
    view.el.style.setProperty('outline-offset', `${offset}px`)

    // Cleanup: remove highlight when hover ends
    return () => {
      if (view && view.el) {
        view.el.classList.remove('object-hover-highlight')
        view.el.style.removeProperty('outline-offset')
      }
    }
  }, [hoveredElementId, graph, paper])
}
